import { BaseService } from './base';
import { Audit } from '../models/audit';

import { ApiRequest } from '../lib/request';
import { ApiListResponse, ApiResponse } from '../lib/response';

export class AuditService extends BaseService<Audit> {
  override model = Audit;
  override modelName = 'audit';
  override modelLabel = 'Audit';

  /**
   * Retrieve audit history for a specific model and primary key
   * 
   * @param modelType - The type/name of the model being audited
   * @param primaryKey - The primary key of the record being audited
   * @param page - Page number for pagination (default: 1)
   * @param attribute - Optional specific attribute to filter by
   * @returns Promise<ApiListResponse<Audit>> - List of audit records
   */
  async history(
    modelType: string,
    primaryKey: string | number,
    page: number = 1,
    attribute?: string
  ): Promise<ApiListResponse<Audit>> {
    const query: Record<string, string> = {
      model: modelType,
      key: primaryKey.toString(),
      page: page.toString(),
    };

    if (attribute) {
      query.attribute = attribute;
    }

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/history`,
      query,
    });

    return await this.requestList<Audit>(request, this.model);
  }

  /**
   * Get all audit history for a model across multiple pages
   * 
   * @param modelType - The type/name of the model being audited
   * @param primaryKey - The primary key of the record being audited
   * @param attribute - Optional specific attribute to filter by
   * @returns Promise<Audit[]> - All audit records for the specified model
   */
  async getAllHistory(
    modelType: string,
    primaryKey: string | number,
    attribute?: string
  ): Promise<Audit[]> {
    let page = 1;
    const allAudits: Audit[] = [];

    while (true) {
      const response = await this.history(modelType, primaryKey, page, attribute);
      
      if (response.success && response.items.length > 0) {
        allAudits.push(...response.items);
        
        // Check if we've reached the last page
        if (response.paging?.isLastPage) {
          break;
        }
        page++;
      } else {
        break;
      }
    }

    return allAudits;
  }

  async revert(
    id: string | number,
    attributes: string[] | Record<string, any>
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${id}/revert`,
      data: { attributes },
    });

    return await this.request(request);
  }
}
